import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import PlayIcon from '@/assets/icons/play-icon.svg?react';
import { useMediaMessageCarousel } from '@/hooks/useMediaMessageCarousel';
import { MessageStatus, type UploadedFile } from '@/types/message.types';
import type { MediaMessageProps } from './types';

const renderLoadingState = () => (
  <div className="sf:absolute sf:inset-0 sf:flex sf:items-center sf:justify-center sf:bg-gray-100 sf:rounded-lg">
    <div className="sf:w-6 sf:h-6 sf:border-2 sf:border-gray-300 sf:border-t-widget-button-color sf:rounded-full sf:animate-spin" />
  </div>
);

const renderImage = ({
  file,
  isLoading,
  gridClasses,
  isMultipleFiles,
  handleLoad,
}: {
  file: UploadedFile;
  isLoading: boolean;
  gridClasses: { item: string; container: string };
  isMultipleFiles: boolean;
  handleLoad: (fileId: string) => void;
}) => (
  <div
    className={classNames({
      'sf:relative sf:overflow-hidden': true,
      [gridClasses.item]: true,
      'sf:rounded-lg': isMultipleFiles,
      'sf:rounded-sm': !isMultipleFiles,
    })}
  >
    {isLoading && renderLoadingState()}

    <img
      src={file.url}
      alt={file.filename || 'Shared image'}
      onLoad={() => handleLoad(file.fileId)}
      className={`sf:w-full sf:h-full sf:object-cover sf:transition-opacity sf:duration-200 sf:hover:opacity-80 ${
        isLoading ? 'sf:opacity-0' : 'sf:opacity-100'
      }`}
    />
  </div>
);

const renderVideo = ({
  file,
  isLoading,
  gridClasses,
  isMultipleFiles,
  handleLoad,
}: {
  file: UploadedFile;
  isLoading: boolean;
  gridClasses: { item: string; container: string };
  isMultipleFiles: boolean;
  handleLoad: (fileId: string) => void;
}) => (
  <div
    className={classNames({
      'sf:relative sf:overflow-hidden': true,
      [gridClasses.item]: true,
      'sf:rounded-lg': isMultipleFiles,
      'sf:rounded-sm': !isMultipleFiles,
    })}
  >
    {isLoading && renderLoadingState()}

    <video
      src={file.url}
      onLoadedData={() => handleLoad(file.fileId)}
      className={`sf:w-full sf:h-full sf:object-cover ${
        isLoading ? 'sf:opacity-0' : 'sf:opacity-100'
      }`}
      preload="metadata"
    />

    {/* Play Button Overlay */}
    {!isLoading && (
      <div className="sf:absolute sf:inset-0 sf:flex sf:items-center sf:justify-center sf:bg-black/20 sf:hover:bg-black/30 sf:transition-colors">
        <div className="sf:w-12 sf:h-12 sf:bg-white/90 sf:rounded-full sf:flex sf:items-center sf:justify-center">
          <PlayIcon className="sf:w-6 sf:h-6 sf:text-widget-button-color sf:ml-1" />
        </div>
      </div>
    )}
  </div>
);

/**
 * MediaMessage - Unified component for rendering both image and video messages
 */
export const MediaMessage = React.memo<MediaMessageProps>(({ message }) => {
  const { actions: carouselActions } = useMediaMessageCarousel();
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {},
  );

  // Filter uploaded files to only image and video files
  const mediaFiles = useMemo(
    () =>
      message?.uploadedFiles?.filter(
        (file: UploadedFile) =>
          file.mimeType?.startsWith('image/') ||
          file.mimeType?.startsWith('video/'),
      ) || [],
    [message?.uploadedFiles],
  );

  // Show maximum 6 items, with a "+N" indicator if there are more
  const { displayFiles, remainingCount, isMultipleFiles } = useMemo(
    () => ({
      displayFiles: mediaFiles.slice(0, 6),
      remainingCount: Math.max(0, mediaFiles.length - 6),
      isMultipleFiles: mediaFiles?.length > 1,
    }),
    [mediaFiles],
  );

  // Initialize loading states for new items
  useEffect(() => {
    const newLoadingStates: Record<string, boolean> = {};

    mediaFiles.forEach((file) => {
      if (!(file.fileId in loadingStates)) {
        newLoadingStates[file.fileId] = true;
      }
    });

    if (Object.keys(newLoadingStates).length > 0) {
      setLoadingStates((prev) => ({ ...prev, ...newLoadingStates }));
    }
  }, [mediaFiles, loadingStates]);

  const handleLoad = useCallback((fileId: string) => {
    setLoadingStates((prev) => ({ ...prev, [fileId]: false }));
  }, []);

  const handleMediaClick = useCallback(
    (index: number) => {
      carouselActions.openCarousel(mediaFiles, index);
    },
    [carouselActions, mediaFiles],
  );

  // Use predefined classes instead of dynamic construction
  const gridClasses = useMemo(() => {
    switch (displayFiles.length) {
      case 1:
        return {
          container: 'sf:grid sf:grid-cols-1 sf:gap-2',
          item: 'sf:w-[268px] sf:h-[150px]',
        };
      case 2:
        return {
          container: 'sf:grid sf:grid-cols-2 sf:gap-2 sf:grid-rows-[96px]',
          item: 'sf:w-[96px] sf:h-[96px]',
        };
      case 3:
        return {
          container: 'sf:grid sf:grid-cols-3 sf:gap-2 sf:grid-rows-[96px]',
          item: 'sf:w-[96px] sf:h-[96px]',
        };
      case 4:
        return {
          container: 'sf:grid sf:grid-cols-2 sf:gap-2 sf:grid-rows-[96px_96px]',
          item: 'sf:w-[96px] sf:h-[96px]',
        };
      default:
        // For 5+ items, show first 6 in 3x2 grid
        return {
          container: 'sf:grid sf:grid-cols-3 sf:gap-2 sf:grid-rows-[96px_96px]',
          item: 'sf:w-[96px] sf:h-[96px]',
        };
    }
  }, [displayFiles.length]);

  if (!mediaFiles.length) {
    return null;
  }

  return (
    <div
      className={classNames({
        'sf:message-bubble sf:rounded-xl sf:shadow-message-bubble sf:overflow-hidden':
          true,
        'sf:p-2': isMultipleFiles,
        'sf:bg-red-10': message?.status === MessageStatus.FAILED,
        'sf:bg-white/60': message?.status !== MessageStatus.FAILED,
      })}
    >
      <div className={gridClasses.container}>
        {displayFiles.map((file, index) => {
          const isImageFile = file.mimeType?.startsWith('image/');
          const isLoading = loadingStates[file.fileId];

          return (
            <div
              key={file.fileId}
              className="sf:relative sf:cursor-pointer"
              onClick={() => handleMediaClick(index)}
            >
              {isImageFile
                ? renderImage({
                    file,
                    isLoading,
                    gridClasses,
                    isMultipleFiles,
                    handleLoad,
                  })
                : renderVideo({
                    file,
                    isLoading,
                    gridClasses,
                    isMultipleFiles,
                    handleLoad,
                  })}

              {/* Show "+N" overlay on the last item if there are more */}
              {index === 5 && remainingCount > 0 && (
                <div className="sf:absolute sf:inset-0 sf:flex sf:items-center sf:justify-center sf:bg-black/40 sf:rounded-xl">
                  <span className="sf:text-white sf:font-semibold sf:text-2xl">
                    +{remainingCount}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

MediaMessage.displayName = 'MediaMessage';
